import React, { useState, useRef, useEffect } from 'react';
import AdminNavbar2 from '../components/AdminNavbar2';
import AdminSidebar2 from '../components/AdminSidebar2';
import { useSidebar } from '../components/SidebarContext';
import axios from 'axios';

const MyAccount2 = () => {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const [profileImage, setProfileImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('Never');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    collegeName: '',
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const fileInputRef = useRef(null);
  const tempImageUrlRef = useRef(null);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const adminToken = localStorage.getItem('collegeAdminToken');
      if (!adminToken) {
        alert('No admin token found. Please login again.');
        return;
      }

      // Try to fetch from API first
      try {
        const response = await axios.get('/api/admin/profile', {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        });

        if (response.data) {
          setFormData({
            username: response.data.username || '',
            email: response.data.email || '',
            collegeName: response.data.collegeName || '',
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          });

          // Set profile image from API response
          if (response.data.profilePhotoUrl && !hasNewImage) {
            setProfileImage(response.data.profilePhotoUrl);
          } else if (!hasNewImage) {
            setProfileImage('/images/admin profile photo.jpg');
          }

          // Set last updated time
          if (response.data.updatedAt) {
            const updatedDate = new Date(response.data.updatedAt);
            setLastUpdated(updatedDate.toLocaleString());
          }

          // Update localStorage with fresh data
          localStorage.setItem('collegeAdminUser', JSON.stringify({
            id: response.data.id,
            username: response.data.username,
            email: response.data.email,
            collegeName: response.data.collegeName,
            profilePhoto: response.data.profilePhoto,
            updatedAt: response.data.updatedAt,
            createdAt: response.data.createdAt,
          }));
        }
      } catch (apiError) {
        console.warn('API fetch failed, falling back to localStorage:', apiError.message);
        
        // Fallback to localStorage
        const adminUserData = localStorage.getItem('collegeAdminUser');
        if (adminUserData && adminUserData !== 'undefined') {
          const parsedUser = JSON.parse(adminUserData);
          setFormData({
            username: parsedUser.username || '',
            email: parsedUser.email || '',
            collegeName: parsedUser.collegeName || '',
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          });

          if (!hasNewImage) {
            setProfileImage('/images/admin profile photo.jpg');
          }

          if (parsedUser.updatedAt) {
            const updatedDate = new Date(parsedUser.updatedAt);
            setLastUpdated(updatedDate.toLocaleString());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error);
      if (!hasNewImage) {
        setProfileImage('/images/admin profile photo.jpg');
      }
      alert('Failed to fetch admin profile data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    return () => {
      if (tempImageUrlRef.current) {
        URL.revokeObjectURL(tempImageUrlRef.current);
        tempImageUrlRef.current = null;
      }
    };
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, JPG, PNG, and GIF files are allowed');
        return;
      }
      
      if (tempImageUrlRef.current) {
        URL.revokeObjectURL(tempImageUrlRef.current);
      }
      
      const imageUrl = URL.createObjectURL(file);
      tempImageUrlRef.current = imageUrl;
      setProfileImage(imageUrl);
      setHasNewImage(true);
      setFormData({ ...formData, profilePhoto: file });
    }
  };

  const handleEditIconClick = () => {
    fileInputRef.current.click();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const { oldPassword, newPassword, confirmNewPassword, profilePhoto } = formData;
    
    // Check if any changes are being made
    const hasPasswordChange = oldPassword || newPassword || confirmNewPassword;
    const hasPhotoChange = profilePhoto;
    
    if (!hasPasswordChange && !hasPhotoChange) {
      alert('Please make at least one change (password or profile photo)');
      return false;
    }

    // Validate password change if any password field is filled
    if (hasPasswordChange) {
      if (!oldPassword) {
        alert('Please enter your old password');
        return false;
      }
      if (!newPassword) {
        alert('Please enter a new password');
        return false;
      }
      if (!confirmNewPassword) {
        alert('Please confirm your new password');
        return false;
      }
      if (newPassword.length < 6) {
        alert('New password must be at least 6 characters long');
        return false;
      }
      if (newPassword === oldPassword) {
        alert('New password cannot be the same as the old password');
        return false;
      }
      if (newPassword !== confirmNewPassword) {
        alert('New password and confirm password do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const adminToken = localStorage.getItem('collegeAdminToken');
    if (!adminToken) {
      alert('No admin token found. Please login again.');
      return;
    }

    setIsUpdating(true);

    const form = new FormData();
    
    // Only append fields that have values
    if (formData.profilePhoto) {
      form.append('profilePhoto', formData.profilePhoto);
    }
    if (formData.oldPassword) {
      form.append('oldPassword', formData.oldPassword);
    }
    if (formData.newPassword) {
      form.append('newPassword', formData.newPassword);
    }
    if (formData.confirmNewPassword) {
      form.append('confirmNewPassword', formData.confirmNewPassword);
    }

    try {
      const response = await axios.post('/api/admin/profile', form, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Profile update response:', response.data);
      
      // Show success message with details of what was changed
      const successMessage = response.data.message || 'Profile updated successfully';
      alert(successMessage);
      
      // Update profile image if a new one was uploaded
      if (formData.profilePhoto && response.data.profilePhotoUrl) {
  // Add timestamp to bypass browser cache
  const imageUrlWithCache = `${response.data.profilePhotoUrl}?t=${Date.now()}`;
  setProfileImage(imageUrlWithCache);
  setHasNewImage(false);
  if (tempImageUrlRef.current) {
    URL.revokeObjectURL(tempImageUrlRef.current);
    tempImageUrlRef.current = null;
  }
}
      
      // Update last updated time
      if (response.data.updatedAt) {
        const updatedDate = new Date(response.data.updatedAt);
        setLastUpdated(updatedDate.toLocaleString());
      }
      
      // Update localStorage with new data
      if (response.data.admin) {
        localStorage.setItem('collegeAdminUser', JSON.stringify(response.data.admin));
        
        // Trigger navbar update
        triggerNavbarUpdate();
      }
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        profilePhoto: null,
      }));
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Error updating admin profile:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update admin profile';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const triggerNavbarUpdate = () => {
  // Dispatch custom event to notify navbar of profile update
  const event = new CustomEvent('adminProfileUpdated');
  window.dispatchEvent(event);
};

  const styles = {
    layout: { display: 'flex', height: '100vh', overflow: 'hidden', minHeight: '100vh', background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)', fontFamily: "'Roboto', sans-serif" },
    sidebar: { position: 'fixed', top: 0, left: 0, height: '100vh', width: sidebarWidth, zIndex: 10, transition: 'width 0.3s ease', borderRadius: '30px', flexShrink: 0, fontSize: '16px' },
    mainContent: { marginLeft: sidebarWidth, flex: 1, transition: 'margin-left 0.3s ease', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    formWrapper: { flex: 1, margin: '6rem 2rem 2rem 2rem', padding: '15px', backgroundColor: 'white', overflowY: 'auto', transition: 'margin-left 0.3s ease', minHeight: 'calc(100vh - 8rem)', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem', maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)', position: 'relative' },
    sectionsContainer: { display: 'flex', flexDirection: 'column', gap: '-3rem', width: '100%', maxWidth: '900px', alignSelf: 'center', marginBottom: '5rem' },
    profilePhotoWrapper: { position: 'relative', width: '100px', height: '100px', flexShrink: 0, marginRight: '1rem' },
    profilePhoto: { width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#6B7280', border: '3px solid #E5E7EB', objectFit: 'cover', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', position: 'relative' },
    profilePhotoImg: { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' },
    loadingSpinner: { fontSize: '12px', color: '#6B7280' },
    editIcon: { position: 'absolute', bottom: '1.5rem', right: '0', transform: 'rotate(180deg)', width: '24px', height: '24px', backgroundColor: '#8B5CF6', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', cursor: 'pointer', transition: 'background-color 0.3s ease, transform 0.3s ease', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' },
    fieldRow: { display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%', padding: '1rem', backgroundColor: '#ffffff', borderRadius: '8px', alignItems: 'center', flexWrap: 'wrap' },
    formColumn: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: '200px', marginTop: 1 },
    usernameColumn: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: '200px', maxWidth: '300px', marginTop: 1 },
    label: { display: 'block', marginBottom: '5px', fontSize: '14px', color: '#6B21A8', fontWeight: '500' },
    input: { width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: '#FFFFFF', transition: 'border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease', boxSizing: 'border-box', color: '#1F2937', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' },
    disabledInput: { width: '100%', padding: '8px 12px', fontSize: '14px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: '#F5F5F5', color: '#6B7280', boxSizing: 'border-box', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)', cursor: 'not-allowed' },
    lastUpdated: { fontSize: '12px', color: '#6B7280', marginTop: '0.5rem', fontStyle: 'italic' },
    bottomRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1rem', width: '100%', maxWidth: '900px', position: 'absolute', bottom: '2.5rem', padding: '0 1rem', justifyContent: 'flex-end' },
    saveButton: { 
      padding: '5px 15px', 
      fontSize: '14px', 
      fontWeight: '600', 
      color: isUpdating ? '#999' : '#6B21A8', 
      textTransform: 'uppercase', 
      letterSpacing: '0.5px', 
      cursor: isUpdating ? 'not-allowed' : 'pointer', 
      borderRadius: '30px', 
      border: '2px solid transparent', 
      backgroundImage: isUpdating ? 'linear-gradient(#f5f5f5, #f5f5f5)' : 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)', 
      backgroundOrigin: 'border-box', 
      backgroundClip: 'padding-box, border-box', 
      transition: 'all 0.4s ease',
      opacity: isUpdating ? 0.6 : 1
    },
  };

  return (
    <div style={styles.layout} className="my-account-wrapper">
      <style>{`
        .my-account-wrapper, .my-account-wrapper * { font-family: 'Roboto', sans-serif !important; }
        .my-account-wrapper .input-field:focus { border-color: #6B21A8 !important; outline: none; box-shadow: 0 0 5px rgba(107, 33, 168, 0.5); }
        .my-account-wrapper .glow-button:hover:not(:disabled) { background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF); transform: scale(1.05); animation: glow 1.5s ease-in-out infinite; }
        .my-account-wrapper .edit-icon:hover { background-color: #A78BFA !important; transform: rotate(360deg); }
        .sidebar-container, .sidebar-container * { font-size: 16px !important; }
        @keyframes glow { 0% { box-shadow: 0 0 5px rgba(107, 33, 168, 0.5); } 50% { box-shadow: 0 0 15px rgba(107, 33, 168, 0.8); } 100% { box-shadow: 0 0 5px rgba(107, 33, 168, 0.5); } }
      `}</style>

      <div style={styles.sidebar} className="sidebar-container">
        <AdminSidebar2 />
      </div>

      <div style={styles.mainContent}>
        <AdminNavbar2 />
        <div style={styles.formWrapper}>
          <div style={styles.sectionsContainer}>
            <div style={styles.fieldRow}>
              <div style={styles.profilePhotoWrapper}>
                <div style={styles.profilePhoto}>
                  {isLoading ? (
                    <div style={styles.loadingSpinner}>Loading...</div>
                  ) : profileImage ? (
                    <img
                      src={profileImage}
                      alt="Admin Profile"
                      style={styles.profilePhotoImg}
                      onError={() => {
                        console.warn('Failed to load admin profile image');
                        setProfileImage('/images/admin profile photo.jpg');
                      }}
                    />
                  ) : (
                    'No Photo'
                  )}
                </div>
                <div
                  style={styles.editIcon}
                  className="edit-icon"
                  onClick={handleEditIconClick}
                  title="Upload admin profile photo"
                >
                  ✎
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
              </div>
              <div style={styles.usernameColumn}>
                <label style={styles.label}>Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
                <div style={styles.lastUpdated}>
                  Last updated: {lastUpdated}
                </div>
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.formColumn}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>College</label>
                <input
                  type="text"
                  name="collegeName"
                  value={formData.collegeName}
                  style={styles.disabledInput}
                  className="input-field"
                  disabled
                />
              </div>
            </div>
            <div style={styles.fieldRow}>
              <div style={styles.formColumn}>
                <label style={styles.label}>Old password</label>
                <input
                  type="password"
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                  placeholder="Enter current password"
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>New password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                  placeholder="Enter new password (min 6 chars)"
                />
              </div>
              <div style={styles.formColumn}>
                <label style={styles.label}>Confirm new password</label>
                <input
                  type="password"
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  className="input-field"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </div>
          <div style={styles.bottomRow}>
            <button
              style={styles.saveButton}
              className={isUpdating ? '' : 'glow-button'}
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount2;